import { BuiltInAttribute, type Priority } from "@orsane/schema/tasks";
import { memo, useCallback } from "react";
import voca from "voca";
import CellButton from "@/components/CellButton";
import { PrioritySelect } from "@/components/property-selectors/PrioritySelect";
import { cn } from "@/lib/utils";
import { useUpdateTaskPropertiesMutation } from "@/mutations/task-mutations";
import type { ColumnDef } from "../ColumnModel";
import type { Row } from "../RowModel";
import { useBulkTargetIds } from "../useBulkTargetIds";

// Static functions - don't need to be recreated
const getSortValue = ({ row }: { row: Row }) => {
    // We do the string prefixing to be able to just sort it alphabetically
    switch (row.data.priority) {
        case "low":
            return "1_low";
        case "medium":
            return "2_medium";
        case "high":
            return "3_high";
        case "urgent":
            return "4_urgent";
        default:
            return "0_none";
    }
};

const getGroupHeader = ({ row }: { row: Row }) => {
    return voca.titleCase(row.data.priority as string) || "None";
};

// Memoized cell component
const PriorityCell = memo(function PriorityCell({
    row,
    onPriorityChange,
}: {
    row: Row;
    onPriorityChange: (taskId: number, priority: Priority | null) => void;
}) {
    const handleChange = useCallback(
        (priority: Priority | null) => {
            onPriorityChange(row.data.id, priority);
        },
        [row.data.id, onPriorityChange]
    );

    return (
        <PrioritySelect
            trigger={(content) => (
                <CellButton disabled={row.data.isNewlyCreated}>
                    {!!row.data.priority && (
                        <div className={cn(" flex items-center gap-1 text-sm", row.data.priority === "urgent" && "text-red-500")}>{content}</div>
                    )}
                </CellButton>
            )}
            value={row.data.priority}
            onChange={handleChange}
        />
    );
});

export function usePriorityColumnBuilder() {
    const { mutate } = useUpdateTaskPropertiesMutation();
    const getTargetIds = useBulkTargetIds();

    // Stable mutation handler
    const handlePriorityChange = useCallback(
        (taskId: number, priority: Priority | null) => {
            for (const id of getTargetIds(taskId)) {
                mutate({
                    id,
                    updates: {
                        priority: priority,
                    },
                });
            }
        },
        [mutate, getTargetIds]
    );

    return {
        build: useCallback(
            (isVisible: boolean): ColumnDef => {
                return {
                    id: BuiltInAttribute.priority,
                    header: "Priority",
                    isVisible: isVisible,
                    cssColDef: "130px",
                    isRight: false,
                    isReadOnly: false,
                    getSortValue: getSortValue,
                    getGroupHeader: getGroupHeader,
                    cell: ({ row }: { row: Row }) => <PriorityCell row={row} onPriorityChange={handlePriorityChange} />,
                };
            },
            [handlePriorityChange]
        ),
    };
}
